import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useMatches, useSearchParams } from "react-router-dom";
import { selectAllCustomerOrganizations } from "../../features/customer/customerOrganizationsSlice";
import { selectAll as selectAllInvoices } from "../../features/invoices/invoiceDetailedSlice";
import { selectJobAdById } from "../../features/jobAds/jobAdSlice";
import { selectOrderRowById } from "../../features/orders/orderRowsDetailedSlice";
import { selectTalentById } from "../../features/talents/talentsSlice";
import { useAppSelector } from "../hooks";
import {
  DEFAULT_DOCUMENT_TITLE,
  resolveDocumentTitle,
} from "./resolveDocumentTitle";

const paramFromMatches = (
  matches: ReturnType<typeof useMatches>,
  key: string,
) => matches.map((match) => match.params[key]).find(Boolean);

export const DocumentTitle = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const matches = useMatches();

  const talentId = paramFromMatches(matches, "talentId");
  const customerCompanyId = paramFromMatches(matches, "customerCompanyId");
  const bundleId = paramFromMatches(matches, "bundleId");
  const invoiceIds = paramFromMatches(matches, "invoiceIds");
  const jobAdId = paramFromMatches(matches, "jobAdId");
  const orgId = searchParams.get("org");
  const singleInvoiceNumber =
    invoiceIds?.split(",").filter(Boolean).length === 1
      ? invoiceIds.split(",").filter(Boolean)[0]
      : undefined;

  const talentName = useAppSelector((state) => {
    if (!talentId) return undefined;
    const talent = selectTalentById(state, talentId);
    const name = [talent?.account?.firstName, talent?.account?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    return name || undefined;
  });

  const customerName = useAppSelector((state) => {
    if (!customerCompanyId) return undefined;
    const organizations = selectAllCustomerOrganizations(state).filter(
      (organization) => organization.customerCompanyId === customerCompanyId,
    );
    const selected = orgId
      ? organizations.find((organization) => organization.id === orgId)
      : undefined;
    const representative = organizations.find(
      (organization) => organization.isRepresentative,
    );
    return (selected ?? representative)?.name || undefined;
  });

  const orderType = useAppSelector((state) => {
    if (!bundleId) return undefined;
    return selectOrderRowById(state, bundleId)?.type;
  });

  const orderCustomerName = useAppSelector((state) => {
    if (!bundleId) return undefined;
    return selectOrderRowById(state, bundleId)?.customerOrgName || undefined;
  });

  const invoiceCustomerName = useAppSelector((state) => {
    if (!singleInvoiceNumber) return undefined;
    return (
      selectAllInvoices(state).find(
        (invoice) => invoice.invoiceNumber === singleInvoiceNumber,
      )?.customerOrganizationName || undefined
    );
  });

  const jobAdTitle = useAppSelector((state) => {
    if (!jobAdId) return undefined;
    return selectJobAdById(state, jobAdId)?.title?.trim() || undefined;
  });

  const title = resolveDocumentTitle(
    {
      pathname,
      talentName,
      customerName,
      orderType,
      orderCustomerName,
      invoiceCustomerName,
      jobAdTitle,
    },
    t,
  );

  useEffect(() => {
    document.title = title ?? DEFAULT_DOCUMENT_TITLE;
    return () => {
      document.title = DEFAULT_DOCUMENT_TITLE;
    };
  }, [title]);

  return null;
};
